
import React, { useState, useRef } from 'react';
import { Button } from './Components';
import { useApp } from './AppContext';

export const ContactPage: React.FC = () => {
  const { sendContactMessage, showToast } = useApp();
  const [sent, setSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState({ name: '', surname: '', email: '', phone: '', message: '', isRegistered: false, mrNumber: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setLoading(true);
    try {
      await sendContactMessage(formData);
      setSubmittedEmail(formData.email);
      setSent(true);
      setFormData({ name: '', surname: '', email: '', phone: '', message: '', isRegistered: false, mrNumber: '' });
    } catch (err) {
      console.error(err);
      showToast("Message Delivery Failed", "We couldn't send your message right now. Please check your connection and try again.", "error");
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 animate-in fade-in duration-500">
      <div className="grid lg:grid-cols-2 gap-24 items-center">
        <div>
          <h1 className="text-6xl font-black text-slate-900 mb-8 leading-tight">We're Here for <br/><span className="text-[#008080]">Your Health</span></h1>
          <p className="text-2xl text-slate-400 mb-12 font-medium">Whether you have a clinical query or media inquiry, our team is standing by.</p>
          <div className="space-y-6">
            <div className="flex items-center gap-8 bg-white p-10 rounded-[3rem] shadow-sm border border-slate-50">
              <div className="w-16 h-16 bg-[#008080]/10 text-[#008080] rounded-2xl flex items-center justify-center text-3xl"><i className="fa-solid fa-envelope"></i></div>
              <div><p className="text-slate-400 font-black text-xs uppercase tracking-widest mb-1">Email Us</p><p className="text-xl font-black text-slate-900">info@rovita.co</p></div>
            </div>
          </div>
        </div>
        <div className="bg-white p-12 md:p-16 rounded-[4.5rem] shadow-2xl border border-slate-50">
          {sent ? (
            <div className="py-12 px-2 text-center animate-in zoom-in duration-500 space-y-6">
                <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center text-4xl mx-auto shadow-xl shadow-emerald-500/10">
                  <i className="fa-solid fa-check"></i>
                </div>
                <h3 className="text-3xl font-bold text-slate-900">Thank You for Contacting Us</h3>
                <p className="text-slate-600 font-medium text-lg max-w-md mx-auto leading-relaxed">
                  We've received your message! A confirmation email has been sent to <strong className="text-slate-900">{submittedEmail}</strong>. One of our representatives or doctors will contact you soon.
                </p>
                <div className="pt-2">
                  <Button onClick={() => setSent(false)} variant="outline" className="px-8 py-4 rounded-2xl">
                    Send another message
                  </Button>
                </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input 
                  className="w-full p-6 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none font-bold transition-all text-lg" 
                  placeholder="First Name" 
                  required 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
                <input 
                  className="w-full p-6 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none font-bold transition-all text-lg" 
                  placeholder="Surname" 
                  required 
                  value={formData.surname}
                  onChange={e => setFormData({...formData, surname: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input 
                  className="w-full p-6 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none font-bold transition-all text-lg" 
                  type="email" 
                  placeholder="Email Address" 
                  required 
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                />
                <input 
                  className="w-full p-6 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none font-bold transition-all text-lg" 
                  type="tel" 
                  placeholder="Phone Number" 
                  required 
                  value={formData.phone}
                  onChange={e => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="p-6 bg-slate-50 rounded-2xl space-y-4">
                <label className="flex items-center gap-4 cursor-pointer group">
                  <div className={`w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all ${formData.isRegistered ? 'bg-[#008080] border-[#008080]' : 'bg-white border-slate-200 group-hover:border-[#008080]'}`}>
                    {formData.isRegistered && <i className="fa-solid fa-check text-white text-sm"></i>}
                  </div>
                  <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={formData.isRegistered}
                    onChange={e => setFormData({...formData, isRegistered: e.target.checked})}
                  />
                  <span className="font-bold text-slate-700 text-lg">Already registered with the platform?</span>
                </label>

                {formData.isRegistered && (
                  <div className="animate-in slide-in-from-top-2 duration-300">
                    <input 
                      className="w-full p-6 bg-white rounded-2xl border-2 border-[#008080]/20 focus:border-[#008080] outline-none font-bold transition-all text-lg" 
                      placeholder="Enter MR Number" 
                      required 
                      value={formData.mrNumber}
                      onChange={e => setFormData({...formData, mrNumber: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <textarea 
                className="w-full p-6 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-[#008080] outline-none h-40 font-bold transition-all resize-none text-lg" 
                placeholder="How can we assist?" 
                required
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
              ></textarea>
              <Button type="submit" disabled={loading} className="w-full py-6 text-xl bg-[#008080] hover:bg-[#006666]">
                {loading ? 'Sending Message...' : 'Send Message'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
